/**
 * Lineups: formation strip (formation + coach per team), a green pitch (home top / away
 * bottom), then a compact two-column Substitutes list — matching the provided design.
 */

import React, { useMemo } from 'react';
import { View } from 'react-native';
import type { MatchHubMockData } from '../../../src/match/types/matchHub.types';
import { getLineupFormationSlots, slotYToPitchBottomHalf, slotYToPitchTopHalf } from './lineupFormationSlots';
import { LineupPitchField } from './LineupPitchField';
import { LINEUP_PITCH_GREEN, LINEUP_PITCH_LINE } from './lineupPitchLayout.constants';
import { MatchHubLineupsFormationStrip } from './MatchHubLineupsFormationStrip';
import { MatchHubLineupsSubs } from './MatchHubLineupsSubs';
import { PitchPlayer } from './MatchHubLineupsPitchPlayer';
import type { MatchHubLineupsStyles } from './MatchHubLineupsPanel.styles';

export interface MatchHubLineupsPitchProps {
  data: MatchHubMockData;
  styles: MatchHubLineupsStyles;
}

export const MatchHubLineupsPitch: React.FC<MatchHubLineupsPitchProps> = ({ data, styles }) => {
  const { lineups, coaches } = data;
  const homeSlots = useMemo(() => getLineupFormationSlots(lineups.home.formation), [lineups.home.formation]);
  const awaySlots = useMemo(() => getLineupFormationSlots(lineups.away.formation), [lineups.away.formation]);
  const hasBench = lineups.home.bench.length > 0 || lineups.away.bench.length > 0;

  return (
    <View style={styles.splitPitchWrap}>
      <MatchHubLineupsFormationStrip
        home={data.home}
        away={data.away}
        homeFormation={lineups.home.formation}
        awayFormation={lineups.away.formation}
        homeCoach={coaches.home.name}
        awayCoach={coaches.away.name}
        styles={styles}
      />

      <View style={[styles.pitchFrame, styles.pitchFrameBorder]}>
        <LineupPitchField lineColor={LINEUP_PITCH_LINE} fillColor={LINEUP_PITCH_GREEN} midLineColor={LINEUP_PITCH_LINE} />
        <View style={styles.pitchPlayersLayer}>
          {lineups.home.starting.map((player, i) => {
            const slot = homeSlots[i];
            if (slot === undefined) return null;
            return (
              <PitchPlayer
                key={`h-${player.id}`}
                player={player}
                xPct={slot.x}
                yPct={slotYToPitchTopHalf(slot.y)}
                kit={data.home.kit}
                styles={styles}
              />
            );
          })}
          {lineups.away.starting.map((player, i) => {
            const slot = awaySlots[i];
            if (slot === undefined) return null;
            return (
              <PitchPlayer
                key={`a-${player.id}`}
                player={player}
                // The away team defends the bottom goal, so rotate its formation
                // 180° — flip x as well as y, otherwise it renders as a mirror
                // image of the top team (left/right wings swapped).
                xPct={100 - slot.x}
                yPct={slotYToPitchBottomHalf(slot.y)}
                kit={data.away.kit}
                styles={styles}
              />
            );
          })}
        </View>
      </View>

      {hasBench && (
        <MatchHubLineupsSubs
          home={data.home}
          away={data.away}
          homeSubs={lineups.home.bench}
          awaySubs={lineups.away.bench}
          styles={styles}
        />
      )}
    </View>
  );
};
